// Mock Authentication Service
// ============================
// This provides a local authentication system for development and testing
// when Firebase is not configured. Uses localStorage to persist state.

import type { User } from '../types';

const STORAGE_KEY = 'lyra_mock_auth';
const USERS_KEY = 'lyra_mock_users';

// Mock user storage
interface StoredUser {
  uid: string;
  email: string;
  password: string;
  displayName: string;
  createdAt: Date;
  onboardingCompleted: boolean;
  preferences: { darkMode: boolean; language: 'da' | 'en' };
  adaptiveLevels: { knowledge: number; language: number; engagement: number };
}

// Get stored users
const getStoredUsers = (): StoredUser[] => {
  try {
    const data = localStorage.getItem(USERS_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
};

// Save users
const saveUsers = (users: StoredUser[]) => {
  localStorage.setItem(USERS_KEY, JSON.stringify(users));
};

// Get current user from storage
export const getCurrentUser = (): User | null => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (data) {
      const user = JSON.parse(data);
      // Convert date string back to Date object
      user.createdAt = new Date(user.createdAt);
      return user;
    }
    return null;
  } catch {
    return null;
  }
};

// Save current user
const saveCurrentUser = (user: User | null) => {
  if (user) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
};

// Generate a simple UID
const generateUid = (): string => {
  return 'mock_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
};

// Sign in
export const mockSignIn = async (email: string, password: string): Promise<{
  success: boolean;
  user?: User;
  error?: string;
}> => {
  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 300));

  const users = getStoredUsers();
  const storedUser = users.find(u => u.email.toLowerCase() === email.toLowerCase());

  if (!storedUser) {
    return { success: false, error: 'Ingen bruger fundet med denne email.' };
  }

  if (storedUser.password !== password) {
    return { success: false, error: 'Forkert adgangskode.' };
  }

  const user: User = {
    uid: storedUser.uid,
    email: storedUser.email,
    displayName: storedUser.displayName,
    createdAt: new Date(storedUser.createdAt),
    onboardingCompleted: storedUser.onboardingCompleted,
    preferences: storedUser.preferences,
    adaptiveLevels: storedUser.adaptiveLevels,
  };

  saveCurrentUser(user);
  return { success: true, user };
};

// Sign up
export const mockSignUp = async (
  email: string,
  password: string,
  displayName: string
): Promise<{
  success: boolean;
  user?: User;
  error?: string;
}> => {
  // Simulate network delay
  await new Promise(resolve => setTimeout(resolve, 300));

  // Validate email
  if (!email || !email.includes('@')) {
    return { success: false, error: 'Ugyldig email adresse.' };
  }

  // Validate password
  if (!password || password.length < 8) {
    return { success: false, error: 'Adgangskoden er for svag. Brug mindst 8 tegn.' };
  }

  const users = getStoredUsers();

  // Check if user exists
  if (users.find(u => u.email.toLowerCase() === email.toLowerCase())) {
    return { success: false, error: 'Denne email er allerede i brug.' };
  }

  const uid = generateUid();
  const newStoredUser: StoredUser = {
    uid,
    email,
    password,
    displayName,
    createdAt: new Date(),
    onboardingCompleted: false,
    preferences: { darkMode: false, language: (localStorage.getItem('lyra-language') as 'da' | 'en') || 'da' },
    adaptiveLevels: { knowledge: 2, language: 2, engagement: 2 },
  };

  users.push(newStoredUser);
  saveUsers(users);

  const user: User = {
    uid: newStoredUser.uid,
    email: newStoredUser.email,
    displayName: newStoredUser.displayName,
    createdAt: newStoredUser.createdAt,
    onboardingCompleted: newStoredUser.onboardingCompleted,
    preferences: newStoredUser.preferences,
    adaptiveLevels: newStoredUser.adaptiveLevels,
  };

  saveCurrentUser(user);
  return { success: true, user };
};

// Sign out
export const mockSignOut = async (): Promise<{ success: boolean }> => {
  await new Promise(resolve => setTimeout(resolve, 100));
  saveCurrentUser(null);
  return { success: true };
};

// Reset password (simulates sending email)
export const mockResetPassword = async (email: string): Promise<{
  success: boolean;
  error?: string;
}> => {
  await new Promise(resolve => setTimeout(resolve, 300));

  const users = getStoredUsers();
  const user = users.find(u => u.email.toLowerCase() === email.toLowerCase());

  if (!user) {
    return { success: false, error: 'Ingen bruger fundet med denne email.' };
  }

  // In a real app, this would send an email
  // For mock purposes, we'll just log it
  console.log(`[Mock Auth] Password reset link would be sent to: ${email}`);
  console.log(`[Mock Auth] Reset link: http://localhost:5173/reset-password?token=mock_${Date.now()}`);

  return { success: true };
};

// Update user profile
export const mockUpdateUser = async (
  uid: string,
  updates: Partial<User>
): Promise<{ success: boolean; error?: string }> => {
  await new Promise(resolve => setTimeout(resolve, 200));

  const users = getStoredUsers();
  const userIndex = users.findIndex(u => u.uid === uid);

  if (userIndex === -1) {
    return { success: false, error: 'Bruger ikke fundet.' };
  }

  // Update stored user
  users[userIndex] = {
    ...users[userIndex],
    ...updates,
  };
  saveUsers(users);

  // Update current user if it's the same user
  const currentUser = getCurrentUser();
  if (currentUser && currentUser.uid === uid) {
    saveCurrentUser({
      ...currentUser,
      ...updates,
    });
  }

  return { success: true };
};

// Delete user account
export const mockDeleteAccount = async (uid: string): Promise<{
  success: boolean;
  error?: string;
}> => {
  await new Promise(resolve => setTimeout(resolve, 300));

  const users = getStoredUsers();
  const userIndex = users.findIndex(u => u.uid === uid);

  if (userIndex === -1) {
    return { success: false, error: 'Bruger ikke fundet.' };
  }

  // Remove user from users list
  users.splice(userIndex, 1);
  saveUsers(users);

  // Clear current user session
  saveCurrentUser(null);

  // Clear user-specific data from localStorage
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.includes(uid)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));

  console.log(`[Mock Auth] Account deleted for uid: ${uid}`);
  return { success: true };
};

// Change user password
export const mockChangePassword = async (
  uid: string,
  currentPassword: string,
  newPassword: string
): Promise<{ success: boolean; error?: string }> => {
  await new Promise(resolve => setTimeout(resolve, 300));

  const users = getStoredUsers();
  const userIndex = users.findIndex(u => u.uid === uid);

  if (userIndex === -1) {
    return { success: false, error: 'Bruger ikke fundet.' };
  }

  // Verify current password
  if (users[userIndex].password !== currentPassword) {
    return { success: false, error: 'Nuværende adgangskode er forkert.' };
  }

  // Validate new password
  if (!newPassword || newPassword.length < 8) {
    return { success: false, error: 'Ny adgangskode skal være mindst 8 tegn.' };
  }

  // Update password
  users[userIndex].password = newPassword;
  saveUsers(users);

  console.log(`[Mock Auth] Password changed for uid: ${uid}`);
  return { success: true };
};

// Auth state change listeners
type AuthListener = (user: User | null) => void;
const listeners: Set<AuthListener> = new Set();

// Subscribe to auth changes
export const onMockAuthChange = (callback: AuthListener): (() => void) => {
  listeners.add(callback);

  // Immediately call with current state
  const currentUser = getCurrentUser();
  callback(currentUser);

  // Return unsubscribe function
  return () => {
    listeners.delete(callback);
  };
};

// Notify listeners (call after any auth state change)
export const notifyAuthListeners = () => {
  const currentUser = getCurrentUser();
  listeners.forEach(callback => callback(currentUser));
};

// Export a function to check if mock auth should be used
export const shouldUseMockAuth = (): boolean => {
  const firebaseApiKey = import.meta.env.VITE_FIREBASE_API_KEY;
  return !firebaseApiKey || firebaseApiKey === 'your_firebase_api_key';
};
